import {
  PropType,
  defineComponent,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { currency } from '@/utils/currency'
import styles from './styles.module.scss'
import { DigitFlip } from '@nio-pow/h5-components' // 使用新数字翻转组件
import { ChargeMapContext } from '../../../chargerMap/index.model'
import { useComponentStore } from '../../../../nsh5/localStores/component'
import { getChargerOrderCount, getIndicatorSummary } from '../../../../apis/map.api'
import { IndicatorItemModel } from '../../soInformation/indicator'
import { WsAlert } from '@/utils/utilsClass/WsAlert'
import { NioApmPlus } from '@nio-fe/nio-apm-plus'
import { getEnv } from '@nio-wad/hooks'
import { isDebugger } from '@/pages/chargermap/utils/getParams'
import { HEmpty } from '@nio-fe/horo-h5'

/**
 * 首页信息功能模块
 */
export type InformationProps = {
  show: () => void
  hide: () => void
}
export default defineComponent({
  name: 'information-mobile-view',
  props: {
    mapContextRef: {
      type: Object as PropType<ChargeMapContext>,
    },
  },

  setup: () => {
    const brand = (process.config as { [key: string]: any }).brand
    const indicator_suffix = (process.config as { [key: string]: any }).indicator_suffix
    const componentStore = useComponentStore()
    // 获取组件配置
    const componentsConfig: any = window.NSComponentsConfig
    // const lang = new URLSearchParams(window.location.search).get('lang')
    // const isEn = componentsConfig?.lang?.show || lang == 'en'
    const state: {
      newNum: string | number | undefined
      newNumCharge: string | number | undefined
      mapChannel: string
      dimCode: string
      dimValue: string
      basicInfo: { [key: string]: IndicatorItemModel }
      showError: boolean
    } = reactive({
      newNum: componentStore.swapNum || '',
      newNumCharge: componentStore.chargeNum || '',
      mapChannel: `${brand}_basic_map`,
      dimCode: 'region_code',
      dimValue: '000000',
      basicInfo: {},
      showError: false,
    })
    const displayDigitFlip = ref(true)
    const displayDigitFlipCharge = ref(true)

    // WsAlert实例
    let newNumAlert: WsAlert | null = null
    let newNumChargeAlert: WsAlert | null = null

    // APM回调
    const newNumCallback = (data?: { realNum: number }) => {
      console.log('newNumCallback triggered:', data)
      console.log('当前newNum实例状态:', {
        realNum: String(newNumAlert?.getRealNum()),
        initNum: String(newNumAlert?.getInitNum()),
        isActive: newNumAlert?.isInstanceActive(),
      })
      try {
        if (getEnv() === 'prod' || isDebugger()) {
          const apmInstance = NioApmPlus.getDefaultInstance()
          apmInstance?.sendEvent({
            name: 'realtime-number-error',
            categories: {
              type: 'swap_number',
              page_source: '充电地图',
              real_number: String(newNumAlert!.getRealNum()),
              init_number: String(newNumAlert!.getInitNum()),
            },
          })
        }
      } catch (error) {}
    }

    const newNumChargeCallback = (data?: { realNum: number }) => {
      console.log('newNumChargeCallback triggered:', data)
      console.log('当前newNumCharge实例状态:', {
        realNum: String(newNumChargeAlert?.getRealNum()),
        initNum: String(newNumChargeAlert?.getInitNum()),
        isActive: newNumChargeAlert?.isInstanceActive(),
      })
      try {
        if (getEnv() === 'prod' || isDebugger()) {
          const apmInstance = NioApmPlus.getDefaultInstance()
          apmInstance?.sendEvent({
            name: 'realtime-number-error',
            categories: {
              type: 'charge_number',
              page_source: '充电地图',
              real_number: String(newNumChargeAlert!.getRealNum()),
              init_number: String(newNumChargeAlert!.getInitNum()),
            },
          })
        }
      } catch (error) {}
    }

    // 离线指标 https://nio.feishu.cn/docx/QjHEdE02UoENAJxBIVwc7hnvn4B
    const STATIC_INDICATOR_CODE_LIST = [
      // 'intercity_swap_station_num', // 高速换电站数量
      // 'swap_order_num', // 累计换电次数
      // 'super_charger_num', // 超充桩数量
      // 'super_charger_station_num', // 超充场站数量
      // 'destination_charger_num', // 目充桩数量
      // 'destination_charger_station_num', // 目充场站数量
      // 'public_charger_num', // 公共桩数量
      // 'swap_station_num_for_com', // 换电站数量
      // 'charge_station_intercity_announcement', // 高速充电站数量
      // 'charge_charger_intercity_announcement', // 高速充电桩数量
      // 'ps_3km_covered_rate', // 电区房覆盖率
      'power_swap_charge_device_num_total', // 蔚来能源充换电站总数
      'swap_station_num_for_com', // 换电站数量
      'intercity_swap_station_num', // 高速换电站数量
      'power_charge_station_device_num_total', // 充电站数量
      'power_charge_device_num_total', // 充电枪数量
      'public_charger_num', // 接入第三方充电桩
      'charger_other_brand_energy_rate', // 蔚来能源充电桩电量第三方用户占比
    ]
    const indicators = STATIC_INDICATOR_CODE_LIST.map(
      (a) => a + (process.config as any).indicator_suffix
    )

    /**
     * 获取summary信息
     */
    let retryNumSummary = 0
    const getInformation = async () => {
      componentStore.selectCityPure('全国')
      const res: any = await getIndicatorSummary(indicators)
      if (res && res.isValid() && res.data) {
        state.showError = false
        componentStore.$patch({ summaryV2: res.data })
        componentStore.setStatisticUpdateTimestamp()

        // 获取离线指标
        indicators.forEach((indicatorCode: string) => {
          const indicatorValue =
            res.data.indicator_map && res.data.indicator_map[indicatorCode]
              ? res.data.indicator_map[indicatorCode]['indicator_item_map'][
                  `${state.dimCode}:${state.dimValue}`
                ] || {}
              : {}
          indicatorValue.indicator_value = currency(indicatorValue.indicator_value || 0)
          state.basicInfo[indicatorCode] = new IndicatorItemModel({ ...indicatorValue })
        })

        // summary接口ready标识
        retryNumSummary = 0
      } else {
        state.showError = true
        // 重试
        if (retryNumSummary < 3) {
          // 重试，1-5秒内随机时间重试
          const randomTime = Math.round(Math.random() * 5 * 1000)
          setTimeout(() => {
            // 重试执行 getComponentsConfig方法
            retryNumSummary++
            getInformation()
          }, randomTime)
        }
      }
    }
    document.onvisibilitychange = () => {
      if (document.visibilityState === 'visible') {
        console.log('====================================');
        console.log('document.visibilityState === visible, informationMobile');
        console.log('====================================');
        componentStore.setStatisticUpdateTimestamp(false);
      }
    };

    /**
     * 获取实时换电和充电数量
     */
    let retryRealTimeNum = 0
    const getRealTimeNumbers = async () => {
      // 如果已有数据，刷新组件显示
      if (componentStore.swapNum) {
        displayDigitFlip.value = false
        nextTick(() => {
          displayDigitFlip.value = true
        })
        state.newNum = currency(String(componentStore.swapNum))
        if (!newNumAlert) {
          newNumAlert = new WsAlert({
            initNum: Number(componentStore.swapNum) || 0,
            currentTime: Date.now(),
            callback: newNumCallback,
          })
        }
      }

      if (componentStore.chargeNum) {
        displayDigitFlipCharge.value = false
        nextTick(() => {
          displayDigitFlipCharge.value = true
        })
        state.newNumCharge = currency(String(componentStore.chargeNum))
        if (!newNumChargeAlert) {
          newNumChargeAlert = new WsAlert({
            initNum: Number(componentStore.chargeNum) || 0,
            currentTime: Date.now(),
            callback: newNumChargeCallback,
          })
        }
      }

      // 如果两个数据都有，直接返回
      if (componentStore.swapNum && componentStore.chargeNum) {
        return
      }

      const res = await getChargerOrderCount({
        area_code: '000000',
        indicator_codes: 'realtime_swap_order_num,realtime_charge_order_num',
      })
      if (res.isValid()) {
        // 处理换电数据
        if (!componentStore.swapNum && res.data.realtimeSwapOrderNum) {
          componentStore.$patch({ swapNum: res.data.realtimeSwapOrderNum.indicatorValue })
          state.newNum = currency(String(res.data.realtimeSwapOrderNum.indicatorValue || 0))
          if (!newNumAlert) {
            newNumAlert = new WsAlert({
              initNum: Number(res.data.realtimeSwapOrderNum.indicatorValue) || 0,
              currentTime: Date.now(),
              callback: newNumCallback,
            })
          }
        }

        // 处理充电数据
        if (!componentStore.chargeNum && res.data.realtimeChargeOrderNum) {
          componentStore.$patch({ chargeNum: res.data.realtimeChargeOrderNum.indicatorValue })
          state.newNumCharge = currency(String(res.data.realtimeChargeOrderNum.indicatorValue || 0))
          if (!newNumChargeAlert) {
            newNumChargeAlert = new WsAlert({
              initNum: Number(res.data.realtimeChargeOrderNum.indicatorValue) || 0,
              currentTime: Date.now(),
              callback: newNumChargeCallback,
            })
          }
        }

        retryRealTimeNum = 0
      } else {
        // 重试
        if (retryRealTimeNum < 3) {
          const randomTime = Math.round(Math.random() * 5 * 1000)
          setTimeout(() => {
            retryRealTimeNum++
            getRealTimeNumbers()
          }, randomTime)
        }
      }
    }

    /**
     * 刷新全国看板数据
     */
    const refreshInformation = async () => {
      getInformation()
      getRealTimeNumbers()
    }

    /**
     * 概览点击阻止冒泡
     */
    const stopPropagation = (e: any) => {
      e.stopPropagation()
    }

    // 跳地址选择
    const selectCity = () => {
      if (componentsConfig?.data_cards?.show) {
        componentStore.bus.emit('openPopup', 'address')
      }
    }

    watch(
      () => componentStore.swapNum,
      () => {
        state.newNum = currency(String(componentStore.swapNum))
        if (newNumAlert && componentStore.swapNum) {
          newNumAlert.updateRealNum(Number(componentStore.swapNum))
        }
      }
    )

    watch(
      () => componentStore.chargeNum,
      () => {
        state.newNumCharge = currency(String(componentStore.chargeNum))
        if (newNumChargeAlert && componentStore.chargeNum) {
          newNumChargeAlert.updateRealNum(Number(componentStore.chargeNum))
        }
      }
    )

    onBeforeMount(() => {
      refreshInformation()
    })

    let dataRefresher: any = null
    onMounted(async () => {
      dataRefresher && clearInterval(dataRefresher)
      dataRefresher = setInterval(async () => {
        refreshInformation()
      }, (componentsConfig?.refresh?.properties?.refresh_interval || 3) * 60 * 1000) // 6小时刷新下首页数据概览
    })

    onBeforeUnmount(() => {
      componentStore.clearStatisticUpdateTimer()
      clearInterval(dataRefresher)
      if (newNumAlert) {
        newNumAlert.compareFn()
        newNumAlert.destroy()
      }
      if (newNumChargeAlert) {
        newNumChargeAlert.compareFn()
        newNumChargeAlert.destroy()
      }
    })

    return () => {
      return state.showError ? (
      <HEmpty
        type="fail_default"
        title="系统开小差了，请稍后重试"
        link-text="刷新"
        onclick={() => refreshInformation()}
      />
      ) : state.basicInfo && Object.keys(state.basicInfo).length > 0 ? (
        <div class={styles.information}>
          {componentsConfig?.data_default?.show ? (
            <div class={styles.data}>
              <div class={styles.plate}>
                <span>资源数据</span>
                <div class={styles.area} onClick={selectCity}>
                  <div>全国</div>
                  {componentsConfig?.data_cards?.show ? <div class={styles.arrow}></div> : null}
                </div>
              </div>
              <div class={styles.dataBox}>
                {state.basicInfo[`power_swap_charge_device_num_total${indicator_suffix}`]
                  ?.indicatorValue !== '0' ? (
                  <section>
                    <div>
                      <div>
                        <h6>蔚来能源充换电站总数</h6>
                        <strong class={styles.totalNum}>
                          {
                            state.basicInfo[`power_swap_charge_device_num_total${indicator_suffix}`]
                              ?.indicatorValue
                          }
                        </strong>
                        <span>座</span>
                      </div>
                    </div>
                  </section>
                ) : null}
                <section>
                  {componentsConfig?.data_default?.properties?.realtime_swap_num === 'on' &&
                  state.newNum &&
                  displayDigitFlip.value ? (
                    <div class={styles.realtime}>
                      <h6>实时累计换电次数</h6>
                      <div class={styles.refresh_outer}>
                        <DigitFlip num={state.newNum} />
                      </div>
                    </div>
                  ) : null}
                  {state.basicInfo[`swap_station_num_for_com${indicator_suffix}`]
                    ?.indicatorValue !== '0' ? (
                    <div class={styles.swap_station_number}>
                      <div>
                        <h6>蔚来能源换电站</h6>
                        <strong class={styles.totalNum}>
                          {
                            state.basicInfo[`swap_station_num_for_com${indicator_suffix}`]
                              ?.indicatorValue
                          }
                        </strong>
                        <span>座</span>
                      </div>
                      {state.basicInfo[`intercity_swap_station_num${indicator_suffix}`]
                        ?.indicatorValue !== '0' ? (
                        <div>
                          <h6>其中高速公路换电站</h6>
                          <strong class={styles.totalNum}>
                            {
                              state.basicInfo[`intercity_swap_station_num${indicator_suffix}`]
                                ?.indicatorValue
                            }
                          </strong>
                          <span>座</span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </section>
                <section>
                  {componentsConfig?.data_default?.properties?.realtime_swap_num === 'on' &&
                  state.newNumCharge &&
                  displayDigitFlipCharge.value ? (
                    <div class={styles.realtime}>
                      <h6>实时累计充电次数</h6>
                      <div class={styles.refresh_outer}>
                        <DigitFlip num={state.newNumCharge} />
                      </div>
                    </div>
                  ) : null}
                  {state.basicInfo[`power_charge_station_device_num_total${indicator_suffix}`]
                    ?.indicatorValue !== '0' ? (
                    <div>
                      <h6>蔚来能源充电站</h6>
                      <strong class={styles.totalNum}>
                        {
                          state.basicInfo[
                            `power_charge_station_device_num_total${indicator_suffix}`
                          ]?.indicatorValue
                        }
                      </strong>
                      <span>座</span>
                      {state.basicInfo[`power_charge_device_num_total${indicator_suffix}`]
                        ?.indicatorValue !== '0' ? (
                        <>
                          &nbsp;/&nbsp;
                          <strong class={styles.totalNum}>
                            {
                              state.basicInfo[`power_charge_device_num_total${indicator_suffix}`]
                                ?.indicatorValue
                            }
                          </strong>
                          <span>根</span>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                  {state.basicInfo[`public_charger_num${indicator_suffix}`]?.indicatorValue !==
                  '0' ? (
                    <div>
                      <h6>接入第三方充电桩</h6>
                      <strong>
                        {state.basicInfo[`public_charger_num${indicator_suffix}`]?.indicatorValue}
                      </strong>
                      <span>根</span>
                    </div>
                  ) : null}
                  {state.basicInfo[`charger_other_brand_energy_rate${indicator_suffix}`]
                    ?.indicatorValue !== '0' ? (
                    <div>
                      <h6>蔚来能源充电桩电量第三方用户占比</h6>
                      <strong>
                        {/* 百分比为非负数，向下取整截断至两位小数，兼容旧版浏览器 */}
                        {state.basicInfo[`charger_other_brand_energy_rate${indicator_suffix}`]
                          ?.indicatorValue
                          ? Math.floor(
                              parseFloat(
                                String(
                                  state.basicInfo[
                                    `charger_other_brand_energy_rate${indicator_suffix}`
                                  ].indicatorValue
                                )
                              ) * 100
                            ) / 100
                          : '-'}
                      </strong>
                      <span>%</span>
                    </div>
                  ) : null}
                  <div class={styles.updateTime}>
                    *截至
                    {componentStore.statisticUpdateTimeString}
                    {brand === 'alps' && '（部分站点升级改造中）'}
                  </div>
                </section>
              </div>
            </div>
          ) : null}
        </div>
      ) : null
    }
  },
})
